/**
 * Remove-pool-account confirm dialog (rev 7, 21/7 — from a live prod screenshot review). A row action
 * next to enable/disable on the Settings "Current pool accounts" table (PoolSettingsPage.tsx), same
 * confirm-then-act shape as ForceActionDialog.tsx: one confirm step, the server's refusal (if any)
 * rendered VERBATIM and in-dialog (role=alert — never a toast, so it can't hide behind the dialog's own
 * top-layer backdrop), success closes the dialog and lets the caller's toast + list refresh happen after.
 *
 * DELETE /pool/accounts/{account_id} removes the account from the pool: sweep stops watching it and its
 * observed Zoom membership rows are dropped, but customer service PERIODS and the audit history for the
 * account are KEPT — this is a pool-membership removal, not a data-destruction op, and the dialog body
 * says so explicitly rather than reading like a generic "are you sure" (see api/types.ts's
 * PoolRemoveAccountResult banner).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiRequestError } from "@/api/client";
import { Button, Modal } from "@/components/ui";
import { useRemovePoolAccount } from "@/features/pool/hooks";
import type { PoolAccount } from "@/api/types";

function errMessage(e: unknown, fallback: string): string {
  return e instanceof ApiRequestError ? e.error.message : fallback;
}

/** account_id is the only field guaranteed non-null — same fallback chain as PoolSettingsPage's own
 *  accountLabel() (duplicated locally, matching this module's tiny-local-helper-per-file convention —
 *  see AddAccountModal.tsx / ForceActionDialog.tsx). */
function accountLabel(a: {
  account_name: string | null;
  account_number: string | null;
  account_id: string;
}): string {
  return a.account_name ?? a.account_number ?? a.account_id;
}

export function RemovePoolAccountDialog({
  account,
  onRemoved,
  onClose,
}: {
  account: PoolAccount;
  /** Called AFTER a successful removal, before onClose — lets the caller fire its own toast (the
   *  accounts/users/dashboard queries are already invalidated by useRemovePoolAccount itself). */
  onRemoved: (membersRemoved: number) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const removeMut = useRemovePoolAccount();
  const [error, setError] = useState<string | null>(null);

  function submit() {
    setError(null);
    removeMut.mutate(account.account_id, {
      onSuccess: (result) => {
        onRemoved(result.members_removed);
        onClose();
      },
      onError: (e) => setError(errMessage(e, t("pool.settings.removeError"))),
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t("pool.settings.removeDialogTitle")}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("action.cancel")}
          </Button>
          <Button variant="danger" loading={removeMut.isPending} onClick={submit}>
            {t("pool.settings.removeButton")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-(length:--text-sm) text-(--text)">
          {t("pool.settings.removeDialogBody", { account: accountLabel(account) })}
        </p>
        {/* The server's refusal rendered VERBATIM — never swallowed into generic copy, same as every
            other refusal in this module (ForceActionDialog's 409/503, PeriodModal's 409, …). */}
        {error && (
          <p role="alert" className="text-(length:--text-sm) text-(--danger)">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
